/**
 * The React half of push: the routing hook the shell mounts, the switch the
 * profile's notification section binds (T51), and the sign-out step.
 */
import { pendingKeys } from "@repo/client/data/plan";
import { workoutKeys } from "@repo/client/data/workout";
import { "push.toast_fallback" as toastFallback } from "@repo/i18n";
import { useQueryClient } from "@tanstack/react-query";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { useOpenLayer } from "../../app/layers";
import { useApi } from "../../data/api";
import { useOutbox } from "../../data/outbox-runner";
import {
  disablePush,
  enablePush,
  getPushState,
  isNative,
  onAppActiveChange,
  type PushState,
  refreshPush,
  subscribePush,
} from "../../native";
import { type SignOutState, useSignOut } from "../auth";
import { shouldRefreshOnResume } from "./consent";
import {
  createPushLayerStore,
  type PushLayerActions,
  type PushLayerStore,
} from "./push-layer";
import {
  type PushRefresh,
  pushRoute,
  refreshForPush,
  toastsInForeground,
} from "./route";
import {
  bootPush,
  channelName,
  onForegroundPush,
  openedInbox,
  unregisterForSignOut,
  writeConsent,
} from "./runtime";

const REFRESH_KEYS: Record<PushRefresh, readonly unknown[]> = {
  pending: pendingKeys.all,
  sessions: workoutKeys.sessionsAll,
};

/** What {@link usePushRouting} hands `PushLayer` (`index.tsx`'s `PushRouting`). */
export interface PushRoutingLayer {
  readonly actions: PushLayerActions;
  readonly store: PushLayerStore;
}

/**
 * Mounted ONCE, inside the session gate (the shell's `PushBridge`, T10), so
 * everything here runs signed in only:
 *
 * - a tapped push navigates with a PUSH (`useOpenLayer`), so Back returns to
 *   where the app was, and invalidates what it announced. A tap that
 *   cold-started the app waited in the inbox and is applied now;
 * - on the device: the boot registration (`refreshPush`, which never
 *   prompts), the one-time rationale before the OS prompt, the foreground
 *   toast, and a `refreshPush` on every resume for a person who opted in.
 *
 * Returns what `PushLayer` draws, or `null` on the web, where every push call
 * is a no-op and nothing is drawn. The store and the actions are created
 * once: `PushLayer`'s toast effect is keyed on `actions`, and a new identity
 * per render would re-notify the toast already on screen.
 */
export function usePushRouting(): PushRoutingLayer | null {
  const { authorizedFetch } = useApi();
  const openLayer = useOpenLayer();
  const queryClient = useQueryClient();

  const refresh = useCallback(
    (kinds: readonly PushRefresh[]) => {
      for (const kind of kinds) {
        queryClient
          .invalidateQueries({ queryKey: REFRESH_KEYS[kind] })
          .catch(() => undefined);
      }
    },
    [queryClient]
  );

  const go = useCallback(
    (data: unknown) => {
      const route = pushRoute(data);
      openLayer(route.search);
      refresh(route.refresh);
    },
    [openLayer, refresh]
  );

  // The `actions` and the long-lived effect below read the latest
  // `go`/`refresh` through refs, so a new callback identity never re-runs the
  // boot registration, and never re-notifies the toast already on screen
  // (`PushLayer`'s effect is keyed on `actions`).
  const goRef = useRef(go);
  const refreshRef = useRef(refresh);
  useEffect(() => {
    goRef.current = go;
    refreshRef.current = refresh;
  }, [go, refresh]);

  useEffect(() => openedInbox.attach((data) => goRef.current(data)), []);

  const [store] = useState(createPushLayerStore);
  const actions = useMemo<PushLayerActions>(
    () => ({
      decline() {
        store.set({ rationaleOpen: false });
        writeConsent("declined").catch(() => undefined);
      },
      enable() {
        store.set({ rationaleOpen: false });
        writeConsent("accepted")
          .then(() => enablePush({ channelName, fetch: authorizedFetch }))
          .catch(() => undefined);
      },
      open(data) {
        goRef.current(data);
      },
    }),
    [store, authorizedFetch]
  );

  useEffect(() => {
    if (!isNative()) {
      return;
    }
    let alive = true;

    const detachForeground = onForegroundPush((message) => {
      refreshRef.current(refreshForPush(message.data));
      if (!toastsInForeground(message.data)) {
        return;
      }
      store.set({
        toast: {
          data: message.data,
          message: message.body ?? message.title ?? toastFallback(),
        },
      });
    });

    bootPush(authorizedFetch)
      .then((ask) => {
        if (alive && ask) {
          store.set({ rationaleOpen: true });
        }
      })
      .catch(() => undefined);

    const stopResume = onAppActiveChange((active) => {
      if (active && shouldRefreshOnResume(getPushState().status)) {
        refreshPush(authorizedFetch).catch(() => undefined);
      }
    });

    return () => {
      alive = false;
      detachForeground();
      stopResume();
    };
  }, [authorizedFetch, store]);

  return isNative() ? { actions, store } : null;
}

export interface PushSwitch {
  /** Turn push off: `DELETE /me/devices`, then forget the token. Never throws. */
  disable(): Promise<PushState>;
  /** Turn push on: channels, the OS prompt if needed, `POST /me/devices`. Never throws. */
  enable(): Promise<PushState>;
  readonly state: PushState;
}

/**
 * The profile's notification switch (T51). `state` is the native
 * registration, live (`subscribePush`/`getPushState`). The answer is also
 * remembered as the rationale's, so a person who switched push off is not
 * asked again at the next launch.
 */
export function usePush(): PushSwitch {
  const { authorizedFetch } = useApi();
  const state = useSyncExternalStore(subscribePush, getPushState, getPushState);

  const enable = useCallback(async () => {
    await writeConsent("accepted");
    return await enablePush({ channelName, fetch: authorizedFetch });
  }, [authorizedFetch]);

  const disable = useCallback(async () => {
    await writeConsent("declined");
    return await disablePush(authorizedFetch);
  }, [authorizedFetch]);

  return { disable, enable, state };
}

/**
 * The push step of a DELIBERATE sign-out: unregister this device while the
 * credential still works, and forget it locally even if the server could
 * not be told. Call it BEFORE `useSession().signOut` — by `onSessionEnded`
 * the credential is gone (`ApiClient.signOut`'s docblock). Never throws.
 */
export function usePushSignOut(): () => Promise<void> {
  const { authorizedFetch } = useApi();
  return useCallback(
    () => unregisterForSignOut(authorizedFetch),
    [authorizedFetch]
  );
}

/**
 * T47's `useSignOut` with the push step in front of it, the same
 * {@link SignOutState} shape, for the profile's account section (T51).
 *
 * The device is unregistered only on a path that really signs out: with
 * unsynced writes the first press only opens the D23 confirmation, and the
 * device is unregistered on "confirm", never on "cancel".
 */
export function useSignOutWithPush(): SignOutState {
  const base = useSignOut();
  const unregister = usePushSignOut();
  const outbox = useOutbox();
  const [unregistering, setUnregistering] = useState(false);
  const unsynced = outbox.pending + outbox.quarantined.length;
  const { signOut: baseSignOut, confirm: baseConfirm } = base;

  const thenSignOut = useCallback(
    (next: () => void) => {
      setUnregistering(true);
      unregister()
        .catch(() => undefined)
        .then(() => {
          setUnregistering(false);
          next();
        });
    },
    [unregister]
  );

  const signOut = useCallback(() => {
    if (unsynced > 0) {
      baseSignOut();
      return;
    }
    thenSignOut(baseSignOut);
  }, [unsynced, baseSignOut, thenSignOut]);

  const confirm = useCallback(
    () => thenSignOut(baseConfirm),
    [thenSignOut, baseConfirm]
  );

  return {
    cancel: base.cancel,
    confirm,
    needsConfirm: base.needsConfirm,
    pending: base.pending || unregistering,
    signOut,
  };
}
